import { throttleTime, filter, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { MatSnackBar } from "@angular/material";
import { Actions, Effect } from "@ngrx/effects";
import { Store } from "@ngrx/store";

import { CAAction } from "../actions";
import {
  Customers,
  CustomerTeam,
  FORCE_LOGOUT_401,
  MemoCategories,
  Organisations,
  RESPONSE_FAIL_AUTH_LOGIN,
  RESPONSE_FAIL_LOCATION,
  RESPONSE_FILE_UPLOAD_ERROR,
  RESPONSE_SUCCESS_AUTH_FORGOT_PASSWORD,
  RESPONSE_SUCCESS_INVITATION_SEND,
  RESPONSE_SUCCESS_INVITATION_RESEND,
  RESPONSE_SUCCESS_INVITATION_CANCEL,
  Staff
} from "../actions/index";
import * as Profile from "../actions/profile.actions";
import {
  NO_ORGANISATIONS,
  NO_USER,
  NO_ROLES,
  FAIL_TOKEN_REFRESH
} from "./startup.effects";

@Injectable()
/**
 * Effects class for deciding whether to show a snackbar when an action occurs
 */
export class SnackBarEffects {
  @Effect({ dispatch: false })
  snacks = this.actions$.pipe(
    tap(action => {
      const message = this.getSnackbarMessageForAction(action);
      if (message) {
        return this.displaySnackbar(message);
      }
    })
  );

  @Effect({ dispatch: false })
  disabledWarn = this.actions$.pipe(
    filter(action => action.type.includes("[Fail]")),
    filter(
      action =>
        action.payload &&
        action.payload.code &&
        action.payload.code.includes("_DISABLED")
    ),
    throttleTime(3000),
    tap(action => {
      switch (action.payload.code) {
        case "ACCOUNT_DISABLED":
          return this.displaySnackbar(
            "Your account appears to be disabled. Speak to an administrator from your organisation"
          );
        case "ORGANISATION_DISABLED":
          return this.displaySnackbar(
            "This organisation appears to be disabled. Speak to a CareApp administrator"
          );
      }
    })
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private snackbar: MatSnackBar
  ) {}

  displaySnackbar(message: string) {
    if (!message) {
      return;
    }
    this.snackbar.open(message, undefined, { duration: 3000 });
  }

  getSnackbarMessageForAction(action) {
    if (
      action.payload &&
      (action.payload.code === "ACCOUNT_DISABLED" ||
        action.payload.code === "ORGANISATION_DISABLED")
    ) {
      return false;
    }

    switch (action.type) {
      case Profile.RESPONSE_SUCCESS_SAVE_PROFILE:
        return `Profile saved`;
      case Profile.RESPONSE_FAIL_SAVE_PROFILE:
        return `Failed to save profile`;
      case RESPONSE_SUCCESS_AUTH_FORGOT_PASSWORD:
        return `Reset instructions sent`;
      case Organisations.SUCCESS_UPDATE_PREFERENCES:
        return `Preferences saved`;
      case CustomerTeam.RESPONSE_SUCCESS_CREATE:
        return `Team member added`;
      case Customers.RESPONSE_FAIL_BULK_ADD_CUSTOMERS:
      case Staff.RESPONSE_FAIL_BULK_IMPORT:
        return "Failed to send upload request to server";
      case Customers.RESPONSE_SUCCESS_CREATE:
        return "Customer Created";
      case RESPONSE_FILE_UPLOAD_ERROR: {
        if (action.payload && action.payload.status === 415) {
          return "That file type is not currently supported by CareApp";
        }
        return "File upload failed for an unknown reason";
      }
      case Organisations.NO_VALID_ORGANISATIONS:
        return "You do not have significant permissions to view any organisations";
      case Organisations.RESPONSE_SUCCESS_CREATE:
        return `Organisation Created`;
      case Staff.RESPONSE_SUCCESS_CREATE:
        return `Staff Member Created`;
      case Staff.RESPONSE_SUCCESS_UPDATE_ROLE:
        return `Staff Permissions Saved`;
      case Staff.RESPONSE_FAIL_UPDATE_ROLE:
        return `Could not update staff permissions at this time`;
      case Organisations.SUCCESS_UPDATE_BRANDING_COLORS:
        return `Branding colours have been saved`;
      case Organisations.SUCCESS_UPDATE_BRANDING_IMAGE:
        return `New branding image has been saved`;
      case Organisations.FAIL_UPDATE_BRANDING_COLORS:
        return `Branding colours could not update at this time`;
      case Organisations.FAIL_UPDATE_BRANDING_IMAGE:
        return `Branding image could not update at this time`;
      case Organisations.RESPONSE_SUCCESS_SAVE:
        return `Organisation Saved`;
      case Customers.RESPONSE_SUCCESS_SAVE:
        return `Customer Saved`;
      case RESPONSE_SUCCESS_INVITATION_SEND:
        return `Staff Member Invited`;
      case RESPONSE_SUCCESS_INVITATION_RESEND:
        return `Invitation Resent`;
      case RESPONSE_SUCCESS_INVITATION_CANCEL:
        return `Invitation cancelled`;
      case RESPONSE_FAIL_LOCATION:
        return `Could not find location`;
      case RESPONSE_FAIL_AUTH_LOGIN: {
        if (
          action.payload &&
          (action.payload.statusCode === 401 ||
            action.payload.statusCode === 403)
        ) {
          return `Login failed. Please check your details and try again.`;
        } else if (action.payload === "Server error") {
          return `Server communication error. Please check your connection and try again.`;
        }
        return false;
      }
      case Customers.RESPONSE_FAIL_ALL:
      case Organisations.RESPONSE_FAIL_ALL:
      case Staff.RESPONSE_FAIL_ALL:
        return `Could not load list. Please try again later`;
      case Customers.RESPONSE_SUCCESS_BULK_ADD_CUSTOMERS:
        return `Community import complete`;
      case Customers.RESPONSE_FAIL_BY_ID:
      case Organisations.RESPONSE_FAIL_BY_ID:
      case Staff.RESPONSE_FAIL_BY_ID:
        return `Could not load that item. Please try again later`;
      case CustomerTeam.RESPONSE_FAIL_CREATE:
        return `Failed to add role. NOTE: Members can only have one role per customer.`;
      case MemoCategories.RESPONSE_SUCCESS_CREATE:
        return `New category created!`;
      case MemoCategories.RESPONSE_FAIL_CREATE:
        return `Failed to create category`;
      case MemoCategories.RESPONSE_SUCCESS_SAVE:
        return `Category updated for all organisations`;
      case MemoCategories.RESPONSE_SUCCESS_REMOVE_BY_ID:
        return `Category removed from organisation.`;
      case MemoCategories.RESPONSE_FAIL_REMOVE_BY_ID:
        return `Could not remove category from organisation. Please try again later.`;
      case MemoCategories.RESPONSE_SUCCESS_ADD_BY_ID:
        return `Category has been updated for this organisation.`;
      case MemoCategories.RESPONSE_FAIL_ADD_BY_ID:
        return `Could not add category from organisation. Please try again later.`;
      case FORCE_LOGOUT_401: {
        switch (action.payload) {
          case NO_ORGANISATIONS:
            return "You do not seem to have sufficient permissions for any organisations. Please speak to an administrator.";
          case NO_USER:
            return "You have been logged out because your user information could not be retrieved";
          case NO_ROLES:
            return "You have been logged out because your role information could not be retrieved";
          case FAIL_TOKEN_REFRESH:
            return "You have been logged out - this might be because of an extended period of innactivity.";
          default:
            return `You have been automatically logged out.`;
        }
      }
    }
    return false;
  }
}
